import React, { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { IconUpload } from '../../redux/groups/groups.model';
import { getPolyglot } from '../../i18n';
import { useAppDispatch } from '../../redux/store.model';
import useShowErrorMessage from '../../handlingErrors/useShowErrorMessage';
import { Group, GroupCreateAPI } from '../../redux/groups/api/group.model';
import * as yup from 'yup';
import {
  putGroup,
  deleteGroup,
  deleteGroupIcon,
} from '../../redux/groups/actions/thunks';
import {
  Box,
  Button,
  DialogContentText,
  Stack,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import SaveIcon from '@mui/icons-material/Save';
import { useShowMessage } from '../../util/hooks';
import { STDialog } from '../commons/Modal/STDialog/STDialog';
import { useNavigationGroupPath } from '../DeviceManager/navigations/navigationPath';
import { useNavigate } from 'react-router';
import { DeleteIcon } from '../../theme/icons';
import { ComposableDeviceValues } from './AddComposableDevice/ComposableDeviceAdd';
import ComposableDeviceForm from './ComposableDeviceForm';

interface ComposableDeviceEditProps {
  group?: Group;
}

export default function ComposableDeviceEdit({
  group,
}: ComposableDeviceEditProps) {
  const polyglot = getPolyglot();

  const dispatch = useAppDispatch();

  const showError = useShowErrorMessage();
  const showSuccess = useShowMessage();
  const [logo, setLogo] = useState<IconUpload | null>(null);
  const [image, setImage] = useState<IconUpload | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const composableDevice = group?.composableDevice;
  const navigationGroupPath = useNavigationGroupPath();
  const navigate = useNavigate();
  const initialValues: ComposableDeviceValues = useMemo(() => {
    setLogo({
      fileName: '',
      toolTip: '',
      file: new File([], '', {}),
      imagePreviewUrl: group?.iconURL ?? '',
    });
    setImage({
      fileName: '',
      toolTip: '',
      file: new File([], '', {}),
      imagePreviewUrl: composableDevice?.labels?.imageURL ?? '',
    });

    return {
      ...group,
      name: group?.name ?? '',
      logoURL: group?.iconURL ?? '',
      imageURL: composableDevice?.labels?.imageURL ?? '',
    };
  }, [composableDevice]);

  const goBackPrevGroup = () => {
    // get 2nd last of list navigationGroupPath, because current group would not exist
    const prevGroupNav = navigationGroupPath[navigationGroupPath.length - 3];
    if (prevGroupNav) return prevGroupNav?.onClick?.();
    navigate(`/`);
  };
  const validationSchema = useMemo(
    () =>
      yup.object({
        name: yup
          .string()
          .required(polyglot.t('composable_device.name_required')),
        devices: yup
          .array()
          .of(yup.string())
          .min(1, polyglot.t('composable_device.devices_required'))
          .required(polyglot.t('composable_device.devices_required')),
      }),
    [polyglot]
  );
  const [newValidationSchema, setNewValidationSchema] =
    useState(validationSchema);
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: newValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values, helpers) => {
      const groupData: GroupCreateAPI = {
        name: values.name,
        devices: values.devices,
        composableDevice: values.composableDevice,
      };

      try {
        await dispatch(
          putGroup(
            group?.id ?? '',
            groupData,
            logo && logo?.fileName !== '' ? logo : undefined
          )
        );

        if (group?.iconURL && !logo?.fileName && formik.values.id) {
          await dispatch(deleteGroupIcon(formik.values.id));
        }
        showSuccess(polyglot.t('composable_device.save_successful_message'));
      } catch (error: any) {
        showError(polyglot.t('composable_device.failed_to_update'));
      }
    },
  });

  const removeGroup = async () => {
    if (group?.id) {
      try {
        setIsLoading(true);
        await dispatch(deleteGroup(group.id));
        setIsOpen(false);
        goBackPrevGroup();
        showSuccess(polyglot.t('composable_device.remove_successful_message'));
      } catch (error: any) {
        showError(polyglot.t('composable_device.failed_to_remove'));
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      <Stack spacing={2}>
        <Typography variant="h2">
          {polyglot
            .t('composable_device.navigation_tooltip')
            .concat(' ')
            .concat(polyglot.t('group.tabs.details').toLocaleLowerCase())}
        </Typography>
        <ComposableDeviceForm
          formik={formik}
          setImage={setImage}
          setLogo={setLogo}
          image={image}
          logo={logo}
          validationSchema={newValidationSchema}
          setValidationSchema={setNewValidationSchema}
        />
        <Box
          sx={{
            display: 'flex',
            mt: 3,
            justifyContent: 'space-between',
          }}
        >
          <LoadingButton
            color="error"
            variant="text"
            startIcon={<DeleteIcon />}
            size="medium"
            type="button"
            loading={formik.isSubmitting || isLoading}
            onClick={() => setIsOpen(true)}
            disabled={formik.isSubmitting || !formik.isValid || isLoading}
          >
            {polyglot.t('button.remove')}
          </LoadingButton>
          <LoadingButton
            color="primary"
            variant="contained"
            size="medium"
            type="submit"
            loading={formik.isSubmitting || isLoading}
            onClick={formik.submitForm}
            startIcon={<SaveIcon />}
            disabled={formik.isSubmitting || !formik.isValid || isLoading}
          >
            {polyglot.t('button.save')}
          </LoadingButton>
        </Box>
      </Stack>
      <STDialog
        polyglot={polyglot}
        title={polyglot.t('composable_device.remove_title')}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        PaperProps={{
          sx: {
            p: '1rem',
            borderRadius: 3,
            boxShadow: (theme) => theme.shadows[5],
          },
        }}
        buttonActions={
          <>
            <Button
              variant="outlined"
              onClick={() => setIsOpen(false)}
              disabled={isLoading}
            >
              {polyglot.t('general.no')}
            </Button>
            <LoadingButton
              color="error"
              loading={isLoading}
              variant="contained"
              onClick={() => {
                removeGroup();
              }}
            >
              {polyglot.t('general.yes')}
            </LoadingButton>
          </>
        }
      >
        <Stack spacing={2}>
          <DialogContentText>
            {polyglot.t('composable_device.remove_message')}
          </DialogContentText>
        </Stack>
      </STDialog>
    </>
  );
}
